<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import mcpAuthorizationsAPI from 'dashboard/api/mcpAuthorizations';

import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();
const route = useRoute();
const currentUser = useMapGetter('getCurrentUser');

const { client_name: clientName, redirect_uri: redirectUri } = route.query;

const adminAccounts = computed(() =>
  (currentUser.value.accounts || []).filter(
    account => account.role === 'administrator'
  )
);
const accountOptions = computed(() =>
  adminAccounts.value.map(account => ({
    value: account.id,
    label: account.name,
  }))
);

const selectedAccountId = ref(adminAccounts.value[0]?.id ?? '');
const isSubmitting = ref(false);

const redirectHost = computed(() => new URL(redirectUri).host);

const redirectBack = params => {
  const url = new URL(redirectUri);
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value);
  });
  window.location.assign(url.toString());
};

const approve = async () => {
  isSubmitting.value = true;
  try {
    const { data } = await mcpAuthorizationsAPI.create(
      selectedAccountId.value,
      {
        client_id: route.query.client_id,
        redirect_uri: redirectUri,
        code_challenge: route.query.code_challenge,
        state: route.query.state,
      }
    );
    window.location.assign(data.redirect_url);
  } catch (error) {
    isSubmitting.value = false;
    useAlert(t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.ERROR'));
  }
};

const deny = () =>
  redirectBack({ error: 'access_denied', state: route.query.state });
</script>

<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full p-4 bg-n-slate-2"
  >
    <div
      class="flex flex-col w-full max-w-md gap-6 p-8 outline outline-n-container outline-1 bg-n-card rounded-xl"
    >
      <div class="flex flex-col gap-2">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ $t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.TITLE', { clientName }) }}
        </h1>
        <p class="text-body-main text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.DESCRIPTION') }}
        </p>
      </div>

      <p v-if="!adminAccounts.length" class="text-body-main text-n-ruby-9">
        {{ $t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.ADMIN_ONLY') }}
      </p>
      <template v-else>
        <div class="flex flex-col gap-2">
          <span class="text-label text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.ACCOUNT') }}
          </span>
          <Select v-model="selectedAccountId" :options="accountOptions" />
        </div>
        <p class="text-sm text-n-slate-11">
          {{
            $t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.REDIRECT', {
              host: redirectHost,
            })
          }}
        </p>
        <div class="flex gap-2">
          <Button
            :label="$t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.APPROVE')"
            :is-loading="isSubmitting"
            blue
            @click="approve"
          />
          <Button
            :label="$t('INTEGRATION_SETTINGS.MCP.AUTHORIZE.DENY')"
            :disabled="isSubmitting"
            faded
            slate
            @click="deny"
          />
        </div>
      </template>
    </div>
  </div>
</template>
